import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Users, Coffee, Wifi, UtensilsCrossed as Restaurant, Waves as Pool, Flower2 as Spa, ImageOff } from 'lucide-react';
import type { Venue } from '../types/venue';
import { useTranslation } from 'react-i18next';
import { OptimizedImage } from './OptimizedImage';

// Helper function to ensure correct image path
const getVenueImagePath = (venue: Venue) => {
  // First try the venue.image property
  if (venue.image) {
    return venue.image;
  }
  
  // Then try the first image in the images array
  if (venue.images && venue.images.length > 0) {
    return venue.images[0];
  }
  
  // If all else fails, use a fallback
  return '/assets/images/placeholder.webp';
};

interface VenueCardProps {
  venue: Venue;
  isRecommended?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  size?: 'small' | 'large';
}

const amenityIcons = {
  wifi: Wifi,
  coffee: Coffee,
  restaurant: Restaurant,
  pool: Pool,
  spa: Spa,
};

export const VenueCard: React.FC<VenueCardProps> = ({ 
  venue, 
  isRecommended, 
  isSelected, 
  onClick,
  size = 'large' 
}) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  
  // Defense against incomplete venue data
  if (!venue) {
    return <div className="venue-card p-3 rounded-lg shadow-md">
      <p>Invalid venue data</p>
    </div>;
  }
  
  // Use the helper function to get the correct image path
  const imageSrc = getVenueImagePath(venue);

  // Much more compact styling for small cards
  const imageHeightClass = size === 'small' ? 'h-24' : 'h-28';
  const cardPaddingClass = size === 'small' ? 'p-2' : 'p-3';
  const titleClass = size === 'small' ? 'text-sm font-semibold' : 'text-base font-semibold';
  const textClass = size === 'small' ? 'text-xs' : 'text-sm';
  const marginClass = size === 'small' ? 'mt-1' : 'mt-2';
  
  return (
    <Link 
      to={`/venues/${venue.id}`} 
      className={`venue-card ${cardPaddingClass} rounded-lg relative ${isRecommended ? 'shadow-[0_0_15px_rgba(250,189,223,0.9)]' : 'shadow-md'} cursor-pointer transition-all ${isSelected ? 'border-2 border-blue-500 bg-blue-50' : 'hover:shadow-lg'}`} 
      onClick={onClick}
    >
      {/* Featured tag for recommended venues */}
      {isRecommended && (
        <div className="absolute top-0 left-0 bg-[#fabddf] text-[#2d2f4e] text-xs font-medium py-1 px-2 rounded-tl-lg rounded-br-lg z-10">
          {t('featured')}
        </div>
      )}
      
      <div className="flex">
        {/* Left side: image */}
        <div className={`${imageHeightClass} w-1/3 overflow-hidden rounded mr-2`}>
          <OptimizedImage
            src={imageSrc} 
            alt={venue.name || 'Venue Image'} 
            className="w-full h-full object-cover"
            width={150}
            height={size === 'small' ? 100 : 120}
            isLCP={isRecommended}
          />
        </div>
        
        {/* Right side: venue info */}
        <div className="venue-info w-2/3">
          <h3 className={`${titleClass} line-clamp-1`}>{venue.name || 'Unnamed Venue'}</h3>
          
          <div className={`venue-location text-gray-600 ${textClass} line-clamp-1`}>
            {venue.city || (venue.address && venue.address.split(',')[1]?.trim()) || 'Unknown Location'}
          </div>
          
          <div className={`venue-capacity ${textClass} text-gray-500`}>
            {venue.capacity?.min || 0}-{venue.capacity?.max || 0} people
          </div>
          
          <div className="flex items-center">
            {/* Stars (without review count) */}
            <div className="venue-rating flex items-center">
              {Array.from({ length: 5 }).map((_, i) => (
                <span key={i} className={`${textClass} ${i < Math.floor(venue.rating || 0) ? 'text-yellow-500' : 'text-gray-300'}`}>★</span>
              ))}
            </div>
          </div>
          
          {/* Amenities row */}
          <div className="venue-amenities flex mt-1">
            {venue.amenities?.slice(0, 3).map(amenity => {
              const IconComponent = amenityIcons[amenity as keyof typeof amenityIcons];
              return IconComponent ? (
                <span key={amenity} className="mr-2 text-gray-600">
                  <IconComponent size={size === 'small' ? 12 : 14} />
                </span>
              ) : null;
            })}
          </div>
        </div>
      </div>
    </Link>
  );
};